import React, { useEffect, useState, useMemo } from "react";
import "./AddCar.css";
import ApiManager from "../../ApiManager/ApiManager";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import { useComponentLoading } from "../../utils/useComponentLoading";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import CustomSelect from "../../components/CustomSelect";

const EditCar = ({ carId: propCarId }) => {
  const { carId: paramCarId } = useParams();
  const id = propCarId || paramCarId;
  const navigate = useNavigate();
  const { loadingStates, loadingMessages, withLoading, getMessage, isLoading } =
    useComponentLoading({
      fetchCar: false,
      fetchDrivers: false,
      fetchCarTypes: false,
      updateCar: false,
    });

  const [vehicleData, setVehicleData] = useState({
    vehicleNo: "",
    vehicleName: "",
    modelName: "",
    color: "",
    fuelType: "",
    capacity: "",
    assignedDriverId: "",
    type: "",
  });
  const [permits, setPermits] = useState([]);
  const [registrationInfo, setRegistrationInfo] = useState({
    ownerName: "",
    registrationDate: "",
    insuranceInfo: {
      insuranceCompany: "",
      insurancePolicyNo: "",
      insuranceIssueDate: "",
      insuranceDueDate: "",
    },
    fitnessInfo: {
      fitnessNumber: "",
      fitnessExpiryDate: "",
    },
    rtoInfo: {
      rtoAddress: "",
      rtoExpiry: "",
    },
  });
  const [driverData, setDriverData] = useState([]);
  const [carTypeData, setCarTypeData] = useState([]);

  const convertTimeToNumber = (inputTime) => {
    if (inputTime) {
      let [time, modifier] = inputTime.split(" ");
      let [hours, minutes] = time.split(":");

      hours = Number(hours);
      minutes = Number(minutes);

      if (modifier?.toLowerCase() === "pm" && hours !== 12) {
        hours += 12;
      } else if (modifier?.toLowerCase() === "am" && hours === 12) {
        hours = 0;
      }

      const formattedTime = `${String(hours).padStart(2, "0")}:${String(
        minutes
      ).padStart(2, "0")}:00`;
      return formattedTime;
    }
    return null;
  };

  const convertDateToString = (inputDate) => {
    if (!inputDate) return null;

    const parts = inputDate.split("-");

    if (parts.length !== 3) return null;

    if (parts[0].length === 4) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`; // Convert to "dd-mm-yyyy"
    } else {
      return `${parts[2]}-${parts[1]}-${parts[0]}`; // Convert to "yyyy-mm-dd"
    }
  };

  // Fetch initial data
  useEffect(() => {
    const fetchCarDetails = async () => {
      try {
        const query = new URLSearchParams({
          carId: id,
        });
        const response = await ApiManager.getCar(query);
        if (!response.ok) throw new Error("Error fetching car details");

        const carDetails = await response.json();
        console.log("Fetched Car Details:", carDetails);

        setVehicleData({
          vehicleNo: carDetails.vehicleNo,
          vehicleName: carDetails.vehicleName,
          modelName: carDetails.modelName,
          color: carDetails.color,
          fuelType: carDetails.fuelType,
          capacity: carDetails.capacity,
          assignedDriverId: carDetails.assignedDriverId,
          type: carDetails.type,
        });

        setPermits(carDetails.permits || []);
        setRegistrationInfo(carDetails.registrationInfo || {});
      } catch (error) {
        console.error("Error fetching car:", error);
      }
    };

    const fetchDriverData = async () => {
      const params = new URLSearchParams({
        page_size: 1000,
        page: 1,
      });
      const response = await ApiManager.getAllDrivers(params);
      const json = await response.json();
      setDriverData(json);
    };

    const fetchCarTypeData = async () => {
      const params = new URLSearchParams({
        page_size: 1000,
        page: 1,
      });
      const response = await ApiManager.getCarType(params);
      const json = await response.json();
      setCarTypeData(json);
    };

    withLoading("fetchCar", fetchCarDetails, "Fetching car details...");
    withLoading("fetchDrivers", fetchDriverData, "Fetching driver data...");
    withLoading("fetchCarTypes", fetchCarTypeData, "Fetching car types...");
  }, [id]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setVehicleData({
      ...vehicleData,
      [name]:
        name === "type" || name === "assignedDriverId" ? Number(value) : value,
    });
  };

  const handlePermitChange = (index, field, value) => {
    const updatedPermits = [...permits];
    updatedPermits[index][field] = value;
    setPermits(updatedPermits);
  };

  const handleRegistrationChange = (section, field, value) => {
    if (section === "ownerName" || section === "registrationDate") {
      setRegistrationInfo({
        ...registrationInfo,
        [section]: value,
      });
    } else {
      setRegistrationInfo({
        ...registrationInfo,
        [section]: {
          ...registrationInfo[section],
          [field]: value,
        },
      });
    }
  };

  const addPermit = () => {
    setPermits([...permits, { permitType: "", expiry: "" }]);
  };

  const removePermit = (index) => {
    const updatedPermits = permits.filter((_, i) => i !== index);
    setPermits(updatedPermits);
  };

  // Handle form submission for updating
  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...vehicleData,
      capacity: parseInt(vehicleData.capacity, 10),
      permits: permits.map((permit) => ({
        permitType: permit.permitType || "",
        expiry: permit.expiry || "",
      })),
      registrationInfo,
    };

    try {
      const query = new URLSearchParams({
        carId: id,
      });
      await withLoading(
        "updateCar",
        async () => {
          const response = await ApiManager.updateCar(query, payload);
          if (!response.ok) throw new Error("Failed to update car details");
          navigate("/login/cars/all", {
            state: {
              toast: {
                type: "success",
                message: "Car details updated successfully!",
              },
            },
          });
        },
        "Updating car details..."
      );
    } catch (error) {
      console.error("Error updating car:", error);
      toast.error("Failed to update car details. Please try again.");
    }
  };

  // Create memoized options for selects
  const fuelTypeOptions = useMemo(() => [
    { value: "Petrol", label: "Petrol" },
    { value: "Diesel", label: "Diesel" },
    { value: "Electric", label: "Electric" },
    { value: "Hybrid", label: "Hybrid" },
    { value: "CNG", label: "CNG" },
    { value: "LPG", label: "LPG" }
  ], []);

  const driverOptions = useMemo(() => 
    (driverData || []).map(driver => ({
      value: driver.id,
      label: driver.firstName
    }))
  , [driverData]);

  const carTypeOptions = useMemo(() => 
    (carTypeData || []).map(carType => ({
      value: carType.id,
      label: carType.name
    }))
  , [carTypeData]);

  if (
    loadingStates.fetchCar ||
    loadingStates.fetchDrivers ||
    loadingStates.fetchCarTypes
  ) {
    return (
      <div>
        <LoadingSpinner
          size="large"
          message={getMessage("fetchCar") || "Loading data..."}
        />
      </div>
    );
  }

  return (
    <div className="addCar">
      <ToastContainer />
      <div style={{ textAlign: "center" }}>
        <h3>Edit Car</h3>
      </div>
      <form onSubmit={handleSubmit}>
        {/* Vehicle Details Section */}
        <fieldset className="fieldset">
          <legend>Vehicle Details</legend>
          <div className="row">
            <div className="formGroup">
              <label>Vehicle No *</label>
              <input
                type="text"
                name="vehicleNo"
                value={vehicleData.vehicleNo}
                onChange={handleInputChange}
                placeholder="Enter vehicle number"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Vehicle Name *</label>
              <input
                type="text"
                name="vehicleName"
                value={vehicleData.vehicleName}
                onChange={handleInputChange}
                placeholder="Enter vehicle name"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Model Name *</label>
              <input
                type="text"
                name="modelName"
                value={vehicleData.modelName}
                onChange={handleInputChange}
                placeholder="Enter model name"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Color *</label>
              <input
                type="text"
                name="color"
                value={vehicleData.color}
                onChange={handleInputChange}
                placeholder="Enter color"
                required
                className="input"
              />
            </div>
          </div>

          <div className="row">
            <div className="formGroup">
              <label>Fuel Type *</label>
              <div style={{ marginTop: "var(--spacing-md)" }}>
                <CustomSelect
                  options={fuelTypeOptions}
                  value={vehicleData.fuelType}
                  onChange={(option) => handleInputChange({
                    target: { name: "fuelType", value: option ? option.value : "" }
                  })}
                  placeholder="Select Fuel Type"
                  required
                />
              </div>
            </div>
            <div className="formGroup">
              <label>Capacity *</label>
              <input
                type="number"
                name="capacity"
                value={vehicleData.capacity}
                onChange={handleInputChange}
                placeholder="Enter seating capacity"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Assigned Driver *</label>
              <div style={{ marginTop: "var(--spacing-md)" }}>
              <CustomSelect
                options={driverOptions}
                value={vehicleData.assignedDriverId}
                onChange={(option) => handleInputChange({
                  target: { name: "assignedDriverId", value: option ? option.value : "" }
                })}
                placeholder="Select Driver"
                required
              />
              </div>
            </div>
            <div className="formGroup">
              <label>Type *</label>
              <div style={{ marginTop: "var(--spacing-md)" }}>
              <CustomSelect
                options={carTypeOptions}
                value={vehicleData.type}
                onChange={(option) => handleInputChange({
                  target: { name: "type", value: option ? option.value : "" }
                })}
                placeholder="Select Type"
                required
              />
              </div>
            </div>
          </div>
        </fieldset>

        {/* Permits Section */}
        <fieldset className="fieldset">
          <legend>Permits</legend>
          {permits.map((permit, index) => (
            <div key={index} className="permitRow">
              <div className="formGroup">
                <label>Permit Type *</label>
                <input
                  type="text"
                  value={permit.permitType}
                  onChange={(e) =>
                    handlePermitChange(index, "permitType", e.target.value)
                  }
                  placeholder="Permit Type"
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Expiry Date *</label>
                <input
                  type="date"
                  value={convertDateToString(permit.expiry)}
                  onChange={(e) =>
                    handlePermitChange(
                      index,
                      "expiry",
                      convertDateToString(e.target.value)
                    )
                  }
                  className="input"
                />
              </div>
              <button
                type="button"
                onClick={() => removePermit(index)}
                className="remove-button"
              >
                x
              </button>
            </div>
          ))}
          <button type="button" onClick={addPermit} className="custom-button">
            + Add Permit
          </button>
        </fieldset>
        {/* Registration Information Section */}
        <fieldset className="fieldset">
          <legend>Registration Info</legend>
          <div className="row">
            <div className="formGroup">
              <label>Owner Name *</label>
              <input
                type="text"
                value={registrationInfo.ownerName}
                onChange={(e) =>
                  handleRegistrationChange(
                    "ownerName",
                    "ownerName",
                    e.target.value
                  )
                }
                placeholder="Owner Name"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Registration Date *</label>
              <input
                type="date"
                value={convertDateToString(registrationInfo.registrationDate)}
                onChange={(e) =>
                  handleRegistrationChange(
                    "registrationDate",
                    "registrationDate",
                    convertDateToString(e.target.value)
                  )
                }
                placeholder="Registration Date"
                required
                className="input"
              />
            </div>
          </div>
          <fieldset className="fieldset">
            <legend>Insurance Info</legend>
            <div className="row">
              <div className="formGroup">
                <label>Insurance Company *</label>
                <input
                  type="text"
                  value={registrationInfo.insuranceInfo.insuranceCompany}
                  onChange={(e) =>
                    handleRegistrationChange(
                      "insuranceInfo",
                      "insuranceCompany",
                      e.target.value
                    )
                  }
                  placeholder="Insurance Company"
                  required
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Insurance Policy No *</label>
                <input
                  type="text"
                  value={registrationInfo.insuranceInfo.insurancePolicyNo}
                  onChange={(e) =>
                    handleRegistrationChange(
                      "insuranceInfo",
                      "insurancePolicyNo",
                      e.target.value
                    )
                  }
                  placeholder="Insurance Policy No"
                  required
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Insurance Issue Date *</label>
                <input
                  type="date"
                  value={convertDateToString(
                    registrationInfo.insuranceInfo.insuranceIssueDate
                  )}
                  onChange={(e) =>
                    handleRegistrationChange(
                      "insuranceInfo",
                      "insuranceIssueDate",
                      convertDateToString(e.target.value)
                    )
                  }
                  required
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Insurance Due Date *</label>
                <input
                  type="date"
                  value={convertDateToString(
                    registrationInfo.insuranceInfo.insuranceDueDate
                  )}
                  onChange={(e) =>
                    handleRegistrationChange(
                      "insuranceInfo",
                      "insuranceDueDate",
                      convertDateToString(e.target.value)
                    )
                  }
                  required
                  className="input"
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="fieldset">
            <legend>Fitness Info</legend>
            <div className="row">
              <div className="formGroup">
                <label>Fitness Number *</label>
                <input
                  type="text"
                  value={registrationInfo.fitnessInfo.fitnessNumber}
                  onChange={(e) =>
                    handleRegistrationChange(
                      "fitnessInfo",
                      "fitnessNumber",
                      e.target.value
                    )
                  }
                  placeholder="Fitness Number"
                  required
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Fitness Expiry Date *</label>
                <input
                  type="date"
                  value={convertDateToString(
                    registrationInfo.fitnessInfo.fitnessExpiryDate
                  )}
                  onChange={(e) =>
                    handleRegistrationChange(
                      "fitnessInfo",
                      "fitnessExpiryDate",
                      convertDateToString(e.target.value)
                    )
                  }
                  required
                  className="input"
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="fieldset">
            <legend>RTO Info</legend>
            <div className="row">
              <div className="formGroup">
                <label>RTO Address *</label>
                <input
                  type="text"
                  value={registrationInfo.rtoInfo.rtoAddress}
                  onChange={(e) =>
                    handleRegistrationChange(
                      "rtoInfo",
                      "rtoAddress",
                      e.target.value
                    )
                  }
                  placeholder="RTO Address"
                  required
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>RTO Expiry Date *</label>
                <input
                  type="date"
                  value={convertDateToString(
                    registrationInfo.rtoInfo.rtoExpiry
                  )}
                  onChange={(e) =>
                    handleRegistrationChange(
                      "rtoInfo",
                      "rtoExpiry",
                      convertDateToString(e.target.value)
                    )
                  }
                  required
                  className="input"
                />
              </div>
            </div>
          </fieldset>
        </fieldset>

        <button
          type="submit"
          className={`submitButton ${isLoading("updateCar") ? "loading" : ""}`}
        >
          {isLoading("updateCar") ? (
            <LoadingSpinner size="xs" inline={true} message="Updating Car..." />
          ) : (
            "Submit"
          )}
        </button>
      </form>
    </div>
  );
};

export default EditCar;
